// src/components/FacilityMap.jsx

import { useMemo } from "react";

import { motion } from "framer-motion";

import {
    Car,
    MapPinned,
    ParkingCircle
} from "lucide-react";

import Vehicle from "./Vehicle";

import {
    getEntryGatePosition,
    getExitGatePosition
} from "../utils/parkingUtils";

export default function FacilityMap({

    facility,

    parkedVehicles = [],

    movingVehicles = []

}) {

    // =========================================
    // LAYOUT
    // =========================================

    const slots =
        facility?.slots || [];

    // =========================================
    // PERFORMANCE LIMIT
    // =========================================

    const MAX_VISIBLE_VEHICLES = 120;

    const visibleParkedVehicles =
        parkedVehicles.slice(
            0,
            MAX_VISIBLE_VEHICLES
        );

    const hiddenVehicleCount =
        Math.max(
            parkedVehicles.length -
            MAX_VISIBLE_VEHICLES,
            0
        );

    // =========================================
    // GATES
    // =========================================

    const entryGate =
        getEntryGatePosition();

    const exitGate =
        getExitGatePosition();

    // =========================================
    // VEHICLE STATUS
    // =========================================

    const isAnyVehicleEntering =
        movingVehicles.some(
            (vehicle) =>
                vehicle.status ===
                "moving"
        );

    const isAnyVehicleExiting =
        movingVehicles.some(
            (vehicle) =>
                vehicle.status ===
                "exiting"
        );

    // =========================================
    // GATE ROTATION
    // =========================================

    const entryGateRotation =
        isAnyVehicleEntering
            ? 0
            : 90;

    const exitGateRotation =
        isAnyVehicleExiting
            ? 0
            : 90;

    // =========================================
    // OCCUPIED SLOT IDS
    // =========================================

    const occupiedSlotIds =
        useMemo(() => {

            return new Set(
                visibleParkedVehicles
                    .filter(
                        (v) =>
                            v.slot_id !==
                            undefined &&
                            v.slot_id !==
                            null
                    )
                    .map(
                        (v) =>
                            v.slot_id
                    )
            );

        }, [visibleParkedVehicles]);

    // =========================================
    // SLOT COLORS
    // =========================================

    const getSlotColor =
        (slotId) => {

            if (
                occupiedSlotIds.has(
                    slotId
                )
            ) {

                return {
                    fill:
                        "#dbeafe",
                    stroke:
                        "#2563eb"
                };
            }

            return {
                fill:
                    "#f8fafc",
                stroke:
                    "#cbd5e1"
            };
        };

    // =========================================
    // EMPTY STATE
    // =========================================

    if (!facility) {

        return (

            <div className="
                w-full
                aspect-[16/9]
                flex
                items-center
                justify-center
                bg-white
                rounded-3xl
                border
                border-gray-200
                shadow-lg
            ">

                <div className="
                    text-center
                ">

                    <MapPinned
                        size={48}
                        className="
                            mx-auto
                            text-gray-300
                            mb-4
                        "
                    />

                    <p className="
                        text-gray-500
                        text-lg
                        font-medium
                    ">
                        Facility data unavailable
                    </p>

                </div>

            </div>
        );
    }

    return (

        <div
            className="
                relative
                w-full
                aspect-[16/9]
                bg-white
                rounded-3xl
                shadow-lg
                overflow-hidden
                border
                border-gray-200
            "
        >

            {/* ================================= */}
            {/* BACKGROUND */}
            {/* ================================= */}

            <div
                className="
                    absolute
                    inset-0
                    opacity-[0.04]
                    bg-cover
                    bg-center
                "
                style={{

                    backgroundImage:
                        facility?.background_image
                            ? `url(${facility.background_image})`
                            : "none"
                }}
            />

            {/* ================================= */}
            {/* HEADER INFO */}
            {/* ================================= */}

            <div
                className="
                    absolute
                    top-4
                    left-4
                    z-30
                    flex
                    flex-wrap
                    gap-3
                "
            >

                <div
                    className="
                        bg-white/95
                        backdrop-blur-sm
                        px-4
                        py-3
                        rounded-2xl
                        shadow-md
                        border
                        border-gray-100
                    "
                >

                    <div className="
                        flex
                        items-center
                        gap-2
                        text-gray-700
                        font-semibold
                        text-sm
                    ">

                        <ParkingCircle
                            size={18}
                        />

                        Slots

                    </div>

                    <div className="
                        text-2xl
                        font-bold
                        text-gray-900
                        mt-1
                    ">

                        {slots.length}

                    </div>

                </div>

                <div
                    className="
                        bg-white/95
                        backdrop-blur-sm
                        px-3
                        py-3
                        rounded-2xl
                        shadow-md
                        border
                        border-gray-100
                    "
                >

                    <div className="
                        flex
                        items-center
                        gap-2
                        text-gray-700
                        font-semibold
                        text-sm
                    ">

                        <Car
                            size={18}
                        />

                        Occupied

                    </div>

                    <div className="
                        text-2xl
                        font-bold
                        text-blue-600
                        mt-1
                    ">

                        {
                            visibleParkedVehicles.length
                        }

                    </div>

                </div>

            </div>

            {/* ================================= */}
            {/* SVG MAP */}
            {/* ================================= */}

            <svg
                viewBox="0 0 1400 800"
                className="
                    w-full
                    h-full
                "
                preserveAspectRatio="xMidYMid meet"
            >

                {/* ============================= */}
                {/* MAIN BACKGROUND */}
                {/* ============================= */}

                <rect
                    x="0"
                    y="0"
                    width="1400"
                    height="800"
                    fill="#f8fafc"
                />

                {/* ============================= */}
                {/* GRID */}
                {/* ============================= */}

                {
                    Array.from({
                        length: 30
                    }).map((_, i) => (

                        <line
                            key={`v-${i}`}
                            x1={i * 50}
                            y1="0"
                            x2={i * 50}
                            y2="800"
                            stroke="#e2e8f0"
                            strokeWidth="1"
                        />

                    ))
                }

                {
                    Array.from({
                        length: 18
                    }).map((_, i) => (

                        <line
                            key={`h-${i}`}
                            x1="0"
                            y1={i * 50}
                            x2="1400"
                            y2={i * 50}
                            stroke="#e2e8f0"
                            strokeWidth="1"
                        />

                    ))
                }

                {/* ============================= */}
                {/* ENTRY ROAD */}
                {/* ============================= */}

                <rect
                    x="0"
                    y="320"
                    width="260"
                    height="160"
                    fill="#cbd5e1"
                    rx="24"
                />

                {/* ============================= */}
                {/* EXIT ROAD */}
                {/* ============================= */}

                <rect
                    x="1140"
                    y="320"
                    width="260"
                    height="160"
                    fill="#cbd5e1"
                    rx="24"
                />

                {/* ============================= */}
                {/* CENTER PARKING AREA */}
                {/* ============================= */}

                <rect
                    x="220"
                    y="80"
                    width="960"
                    height="640"
                    fill="#e2e8f0"
                    stroke="#94a3b8"
                    strokeWidth="4"
                    rx="36"
                />

                {/* ============================= */}
                {/* INTERNAL DRIVING LANES */}
                {/* ============================= */}

                <line
                    x1="700"
                    y1="100"
                    x2="700"
                    y2="700"
                    stroke="#94a3b8"
                    strokeWidth="14"
                    strokeDasharray="30 20"
                />

                <line
                    x1="240"
                    y1="400"
                    x2="1160"
                    y2="400"
                    stroke="#94a3b8"
                    strokeWidth="14"
                    strokeDasharray="30 20"
                />

                {/* ============================= */}
                {/* ENTRY LABEL */}
                {/* ============================= */}

                <text
                    x="110"
                    y="300"
                    fontSize="28"
                    fill="#166534"
                    fontWeight="bold"
                    textAnchor="middle"
                >

                    ENTRY

                </text>

                {/* ============================= */}
                {/* EXIT LABEL */}
                {/* ============================= */}

                <text
                    x="1290"
                    y="300"
                    fontSize="28"
                    fill="#991b1b"
                    fontWeight="bold"
                    textAnchor="middle"
                >

                    EXIT

                </text>

                {/* ENTRY GATE */}
                <g
                    transform={`
        translate(150 310)
        rotate(${entryGateRotation})
    `}
                    style={{
                        transition:
                            "transform 1s ease-in-out"
                    }}
                >

                    {/* Hinge */}
                    <circle
                        cx="0"
                        cy="0"
                        r="10"
                        fill="#166534"
                    />

                    {/* Barrier */}
                    <rect
                        x="0"
                        y="-7"
                        width="180"
                        height="14"
                        rx="999"
                        fill="#22c55e"
                    />

                    {/* Stripe Overlay */}
                    <rect
                        x="0"
                        y="-7"
                        width="180"
                        height="14"
                        rx="999"
                        fill="url(#entryPattern)"
                        opacity="0.45"
                    />

                </g>

                {/* EXIT GATE */}
                <g
                    transform={`
        translate(1250 310)
        rotate(${-exitGateRotation})
    `}
                    style={{
                        transition:
                            "transform 1s ease-in-out"
                    }}
                >

                    {/* Hinge */}
                    <circle
                        cx="0"
                        cy="0"
                        r="10"
                        fill="#991b1b"
                    />

                    {/* Barrier */}
                    <rect
                        x="-180"
                        y="-7"
                        width="180"
                        height="14"
                        rx="999"
                        fill="#ef4444"
                    />

                    {/* Stripe Overlay */}
                    <rect
                        x="-180"
                        y="-7"
                        width="180"
                        height="14"
                        rx="999"
                        fill="url(#exitPattern)"
                        opacity="0.45"
                    />

                </g>

                {/* ============================= */}
                {/* PATTERNS */}
                {/* ============================= */}

                <defs>

                    <pattern
                        id="entryPattern"
                        width="20"
                        height="20"
                        patternUnits="userSpaceOnUse"
                        patternTransform="rotate(45)"
                    >

                        <rect
                            width="10"
                            height="20"
                            fill="white"
                        />

                    </pattern>

                    <pattern
                        id="exitPattern"
                        width="20"
                        height="20"
                        patternUnits="userSpaceOnUse"
                        patternTransform="rotate(45)"
                    >

                        <rect
                            width="10"
                            height="20"
                            fill="white"
                        />

                    </pattern>

                </defs>

                {/* ============================= */}
                {/* SLOT RENDERING */}
                {/* ============================= */}

                {
                    slots.map((slot) => {

                        const slotStyle =
                            getSlotColor(
                                slot.slot_id
                            );

                        const rotation =
                            slot.rotation || 0;

                        const width =
                            slot.width || 90;

                        const height =
                            slot.height || 45;

                        const centerX =
                            slot.slot_center_x || 0;

                        const centerY =
                            slot.slot_center_y || 0;

                        const polygon =
                            slot.polygon_points || [];

                        // =========================
                        // POLYGON SLOT
                        // =========================

                        if (
                            polygon.length >= 3
                        ) {

                            const points =
                                polygon.map(
                                    (p) =>
                                        `${p.x},${p.y}`
                                ).join(" ");

                            return (

                                <g
                                    key={
                                        slot.slot_id
                                    }
                                >

                                    <polygon
                                        points={
                                            points
                                        }
                                        fill={
                                            slotStyle.fill
                                        }
                                        stroke={
                                            slotStyle.stroke
                                        }
                                        strokeWidth="3"
                                    />

                                    <text
                                        x={centerX}
                                        y={centerY}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fontSize="14"
                                        fill="#334155"
                                        fontWeight="700"
                                    >
                                        {
                                            slot.slot_code
                                        }
                                    </text>

                                </g>
                            );
                        }

                        // =========================
                        // RECT SLOT
                        // =========================

                        return (

                            <g
                                key={
                                    slot.slot_id
                                }
                                transform={`
                                    rotate(
                                        ${rotation},
                                        ${centerX},
                                        ${centerY}
                                    )
                                `}
                            >

                                <rect
                                    x={
                                        centerX -
                                        width / 2
                                    }
                                    y={
                                        centerY -
                                        height / 2
                                    }
                                    width={width}
                                    height={height}
                                    rx="8"
                                    fill={
                                        slotStyle.fill
                                    }
                                    stroke={
                                        slotStyle.stroke
                                    }
                                    strokeWidth="3"
                                />

                                <text
                                    x={centerX}
                                    y={centerY}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize="14"
                                    fill="#334155"
                                    fontWeight="700"
                                >

                                    {
                                        slot.slot_code
                                    }

                                </text>

                            </g>
                        );
                    })
                }

                {/* ============================= */}
                {/* MOVEMENT PATHS */}
                {/* ============================= */}

                {
                    movingVehicles.map(
                        (vehicle) => {

                            const pathStartX =
                                entryGate.x;

                            const pathStartY =
                                entryGate.y;

                            const pathEndX =
                                vehicle.x;

                            const pathEndY =
                                vehicle.y;

                            if (
                                vehicle.status ===
                                "moving"
                            ) {

                                return (

                                    <path
                                        key={`
                                            ${vehicle.id}-entry
                                        `}
                                        d={`
                                            M ${pathStartX} ${pathStartY}
                                            L ${pathEndX} ${pathStartY}
                                            L ${pathEndX} ${pathEndY}
                                        `}
                                        fill="none"
                                        stroke="#38bdf8"
                                        strokeWidth="6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeDasharray="14 10"
                                        opacity="0.55"
                                    />

                                );
                            }

                            if (
                                vehicle.status ===
                                "exiting"
                            ) {

                                const extendedExitX =
                                    exitGate.x + 180;

                                return (

                                    <path
                                        key={`
                ${vehicle.id}-exit
            `}
                                        d={`
                M ${pathEndX} ${pathEndY}
                L ${pathEndX} ${pathStartY}
                L ${extendedExitX} ${exitGate.y}
            `}
                                        fill="none"
                                        stroke="#f87171"
                                        strokeWidth="6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeDasharray="14 10"
                                        opacity="0.55"
                                    />

                                );
                            }

                            return null;
                        }
                    )
                }

                {/* ============================= */}
                {/* PARKED VEHICLES */}
                {/* ============================= */}

                {
                    visibleParkedVehicles.map(
                        (vehicle) => (

                            <Vehicle
                                key={vehicle.id}
                                vehicle={vehicle}
                            />

                        )
                    )
                }

                {/* ============================= */}
                {/* MOVING VEHICLES */}
                {/* ============================= */}

                {
                    movingVehicles.map(
                        (vehicle) => (

                            <Vehicle
                                key={vehicle.id}
                                vehicle={vehicle}
                                moving={true}
                            />

                        )
                    )
                }

            </svg>

            {/* ================================= */}
            {/* LEGEND */}
            {/* ================================= */}

            <div
                className="
                    absolute
                    bottom-4
                    left-4
                    z-30
                    bg-white/95
                    backdrop-blur-sm
                    border
                    border-gray-200
                    shadow-md
                    rounded-2xl
                    px-4
                    py-3
                    flex
                    items-center
                    gap-5
                    text-sm
                    font-medium
                "
            >

                <div className="
                    flex
                    items-center
                    gap-2
                ">

                    <div className="
                        w-4
                        h-4
                        rounded
                        bg-slate-100
                        border
                        border-slate-300
                    " />

                    Vacant

                </div>

                <div className="
                    flex
                    items-center
                    gap-2
                ">

                    <div className="
                        w-4
                        h-4
                        rounded
                        bg-blue-100
                        border
                        border-blue-600
                    " />

                    Occupied

                </div>

            </div>

            {/* ================================= */}
            {/* HIDDEN VEHICLES */}
            {/* ================================= */}

            {
                hiddenVehicleCount > 0 && (

                    <div
                        className="
                            absolute
                            top-4
                            right-4
                            bg-blue-100
                            text-blue-700
                            px-4
                            py-2
                            rounded-xl
                            text-sm
                            font-semibold
                            shadow-md
                            z-30
                        "
                    >

                        +{hiddenVehicleCount}
                        {" "}
                        more vehicles

                    </div>

                )
            }

        </div>
    );
}